import { useMemo, useState } from "react";
import { Input } from "../ui/Input";
import { Search, Layers } from "lucide-react";

interface CourseOption {
  _id: string;
  title: string;
  price?: number;
  level?: string;
}

interface CourseMultiSelectProps {
  options: CourseOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

export function CourseMultiSelect({
  options,
  selectedIds,
  onChange,
}: CourseMultiSelectProps) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return options.filter((option) =>
      option.title.toLowerCase().includes(term)
    );
  }, [options, search]);

  const toggleId = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((courseId) => courseId !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="space-y-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-900">Courses</p>
          <p className="text-xs text-gray-500">
            Select at least two courses to include in this package.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-600">
          <Layers size={14} />
          {selectedIds.length} selected
        </div>
      </div>

      <Input
        placeholder="Search courses..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        icon={<Search size={14} />}
      />

      <div className="max-h-64 space-y-2 overflow-y-auto pr-2">
        {filtered.length === 0 && (
          <p className="py-6 text-center text-sm text-gray-400">
            No courses found
          </p>
        )}

        {filtered.map((course) => (
          <label
            key={course._id}
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2 transition hover:bg-white"
          >
            <input
              type="checkbox"
              checked={selectedIds.includes(course._id)}
              onChange={() => toggleId(course._id)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                {course.title}
              </p>
              <p className="text-xs text-gray-500">
                {course.level ? `${course.level} • ` : ""}
                {course.price ? `$${course.price?.toFixed(2)}` : "Free"}
              </p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}
